import axios from "axios";
import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import Head from "next/head";
import MasterclassCard from "@/components/MasterclassCard";
import { motion } from "framer-motion";
import { useRouter } from "next/router";
import webengage from "@/webengage";
import { NextPageContext } from "next";
import { cn } from "@/lib/utils";
import { ArrowLeftIcon } from "lucide-react";
import { SelectDropDown } from "@/components/ui/SelectDropDown";
import Breadcrumb from "@/components/BreadCrumb";

export async function getServerSideProps(ctx: NextPageContext) {
  try {
    const subCategory = ctx.query.category as string;
    const spiritualityId = "clcj0zqjl000ogr0qnev4m235";

    const { data: fetchedSubCategories } = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_API}categories/sub-categories/${spiritualityId}`,
      {
        headers: { "Accept-Encoding": "gzip,deflate,compress" },
      }
    );

    let masterclassUrl = `${process.env.NEXT_PUBLIC_BASE_API}masterclasses?select=slots&getFutureSlots=true&category=Spirituality`;
    if (subCategory && typeof subCategory === "string") {
      const sanitizedSubCategory = subCategory.replace(/%/g, "&");
      const subCategoryId = fetchedSubCategories.data.find(
        (tag: any) => tag.slug === sanitizedSubCategory
      )?.id;

      if (subCategoryId) {
        masterclassUrl = `${process.env.NEXT_PUBLIC_BASE_API}masterclasses/masterclass-by-subcategory/${subCategoryId}?getFutureSlots=true`;
      }
    }

    const fetchedMasterclasses = await axios.get(masterclassUrl, {
      headers: { "Accept-Encoding": "gzip,deflate,compress" },
    });

    return {
      props: {
        masterclasses: fetchedMasterclasses.data,
        tags: fetchedSubCategories.data,
      },
    };
  } catch (err) {
    console.log(err);
    return {
      props: {
        masterclasses: [],
        tags: [],
      },
    };
  }
}

export default function Masterclasses({
  masterclasses,
  tags,
}: {
  masterclasses: any;
  tags: any;
}) {
  const router = useRouter();
  const [activeCategory, setActiveCategory] = useState<{
    name: string;
    id: string;
    slug: string;
  } | null>(null);

  const tagsArray = tags || [];
  const TAGS: any = {};
  tagsArray.forEach((tag: any) => {
    TAGS[tag.slug.replace(/&/g, "%")] = {
      name: tag?.name,
      id: tag?.id,
      slug: tag?.slug.replace(/&/g, "%"),
    };
  });

  useEffect(() => {
    setActiveCategory(
      router.query.category ? TAGS[router.query.category.toString()] : null
    );
  }, [router.query.category]);

  useEffect(() => {
    const _webengage = webengage();
    if (_webengage)
      _webengage.track("Masterclass Page viewed", {
        source: "Astrolearn",
      });
  }, []);

  return (
    <Layout>
      <Head>
        <title>
          Free Astrology Classes Online | Occult Courses Online | Astrolearn
        </title>
        <meta
          name="description"
          content="Learn astrology and occult sciences online. Join live classes on predictive sciences, healing, manifestation, and mystic arts. Get certified today!"
        />
      </Head>

      <div className="font-lato p-4">
        <Breadcrumb />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ ease: "linear", duration: 0.25 }}
          className="flex flex-col gap-4 md:flex-row md:justify-between mt-1"
        >
          <div className="flex gap-4 justify-start items-center">
            <ArrowLeftIcon
              className="w-4 h-4 hover:cursor-pointer md:hidden"
              onClick={() => router.back()}
            />
            <h3 className="text-[#1D2026] max-lg:text-xl font-bold">
              Explore Free Masterclasses
            </h3>
          </div>
          <div className="max-md:w-full md:max-w-[900px] flex gap-2 sm:gap-4 justify-center md:justify-end">
            <button
              onClick={() => router.push("/masterclasses")}
              className={cn(
                "rounded-full whitespace-nowrap text-sm md:text-base px-2 md:px-3.5 py-1 md:py-1.5 font-medium capitalize border-none",
                !activeCategory ? "bg-[#FFD700]" : "bg-[#9A702633]"
              )}
            >
              All
            </button>
            {tagsArray.map((tag: any) => (
              <button
                onClick={() => {
                  const _webengage = webengage();
                  if (_webengage) {
                    _webengage.track("Category Clicked", {
                      industry: tag.name,
                      source: "Astrolearn",
                    });
                  }

                  const sanitizedSlug = tag.slug.replace(/&/g, "%");
                  router.push(`/masterclasses?category=${sanitizedSlug}`);
                }}
                className={cn(
                  "rounded-full text-sm md:text-base px-2 md:px-3.5 py-1 md:py-1.5 font-medium capitalize border-none whitespace-nowrap",
                  tag?.name === activeCategory?.name
                    ? "bg-[#FFD700]"
                    : "bg-[#9A702633]"
                )}
                key={tag?.name}
              >
                {tag?.name}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="flex flex-col mt-4 py-2 gap-8 md:grid md:grid-cols-2 lg:grid-cols-3 justify-center items-center">
          {masterclasses?.data?.map((masterclass: any, index: number) => (
            <MasterclassCard
              initial={{ opacity: 0, translateY: 40 }}
              animate={{ opacity: 1, translateY: 0 }}
              transition={{ duration: 0.4, delay: 0.2 * index, type: "tween" }}
              viewport={{ once: true }}
              masterclass={masterclass}
              key={masterclass?.id}
            />
          ))}
        </div>
      </div>
    </Layout>
  );
}
